import { Minus, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { QuantityInput } from '@/components/ui/quantity-input'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

interface Props {
    id: string
    /** Chuỗi đang gõ; rỗng = 0. */
    value: string
    onChange: (value: string) => void
    max: number
    unit?: string
    label: string
    invalid?: boolean
    describedBy?: string
    size?: 'md' | 'sm'
}

function toNumber(raw: string): number {
    const n = Number(raw)
    return raw.trim() === '' || !Number.isFinite(n) ? 0 : n
}

/**
 * Ô số lượng có nút −/+ cho popup trả hàng (M-44). Vùng chạm ≥ 44px trên
 * mobile; ô vẫn gõ tay được (số lẻ kg). Không chặn số vượt max — form báo lỗi
 * cạnh ô để người dùng biết vì sao.
 */
export function ReturnQtyStepper({
    id,
    value,
    onChange,
    max,
    unit,
    label,
    invalid,
    describedBy,
    size = 'md',
}: Props) {
    const current = toNumber(value)
    const step = (delta: number) => {
        const next = Math.max(0, Math.min(max, current + delta))
        onChange(next === 0 ? '' : String(Number(next.toFixed(3))))
    }
    const box = size === 'md' ? 'size-11' : 'size-10'
    return (
        <div className="flex items-center gap-1.5">
            <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(box, 'shrink-0')}
                aria-label={m.sales_return_decrease({ label })}
                disabled={current <= 0}
                onClick={() => step(-1)}
            >
                <Minus className="size-4" />
            </Button>
            <QuantityInput
                id={id}
                type="number"
                inputMode="decimal"
                min={0}
                unit={unit}
                aria-label={label}
                aria-invalid={invalid}
                aria-describedby={describedBy}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={cn(
                    'text-center text-base tabular-nums',
                    size === 'md' ? 'h-11 w-24' : 'h-10 w-20',
                )}
            />
            <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(box, 'shrink-0')}
                aria-label={m.sales_return_increase({ label })}
                disabled={current >= max}
                onClick={() => step(1)}
            >
                <Plus className="size-4" />
            </Button>
        </div>
    )
}
